import { Check, ChevronDown, FolderOpen, GitBranch, GitCommitHorizontal, Search } from 'lucide-react';
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { api } from '../modules/platform/index.ts';
import { DEFAULT_BRANCH, parseProjectPath, projectPath } from '../modules/platform/index.ts';
import type { Project } from '../modules/workspace/index.ts';
import { MENU_ITEM_CLASS } from './UserMenu.tsx';
import { useCommitView } from '../modules/platform/index.ts';

const CHIP =
  'flex items-center gap-2 h-7 px-2 rounded-[10px] bg-white/10 hover:bg-white/15 text-white text-xs no-underline transition-colors';

/** Advanced mode: project chip → Projects, branch chip → a branch and commit switcher (or a "Create project" CTA). */
export function ProjectBranchChip({ slug, branch }: { slug: string | null; branch: string | null }) {
  if (!slug) {
    return (
      <Link
        to="/"
        title="Create your first project"
        data-tour="project-branch-chip"
        data-testid="project-branch-chip"
        className={`${CHIP} text-white/80`}
      >
        <FolderOpen size={14} className="shrink-0" />
        <span className="font-medium">Create project</span>
      </Link>
    );
  }
  return (
    <div
      className="flex items-center gap-1"
      data-tour="project-branch-chip"
      data-testid="project-branch-chip"
    >
      <Link to={projectPath('projects', slug, branch)} title="Manage projects" className={CHIP}>
        <FolderOpen size={14} className="shrink-0" />
        <span className="font-medium truncate max-w-[120px]" data-testid="chip-project">
          {slug}
        </span>
      </Link>
      <span className="text-white/50 text-xs">/</span>
      <BranchSwitcher slug={slug} branch={branch || DEFAULT_BRANCH} />
    </div>
  );
}

interface LogCommit {
  sha: string;
  message: string;
  date: string;
  author: { name: string; email: string };
}

const agoShort = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

/**
 * The branch chip: a searchable switcher with two tabs. Branches: the project's branches. Commits: the
 * recent commits of the current branch (a commit's home branch when at one), searchable by message,
 * sha or author; picking one opens the same page at that commit, read-only (the newest, the branch's head, opens
 * the branch). Either keeps the current
 * page. At a commit the chip shows the short sha. "All branches" opens the Code page's branch list.
 */
function BranchSwitcher({ slug, branch }: { slug: string; branch: string }) {
  const { info, atCommit } = useCommitView();
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState<'branches' | 'commits'>('branches');
  const [q, setQ] = React.useState('');
  const [list, setList] = React.useState<{ names: string[]; defaultBranch: string } | null>(null);
  const [commits, setCommits] = React.useState<LogCommit[] | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  /** The branch whose commits the Commits tab lists: the current branch, or the commit's home branch. */
  const logBranch = atCommit && info ? info.branch : branch;

  React.useEffect(() => {
    if (!open) return;
    setQ('');
    setTab(atCommit ? 'commits' : 'branches');
    let alive = true;
    api
      .GET('/api/workspace/projects/{slug}/branches', { params: { path: { slug } } })
      .then(({ data }) => {
        if (alive)
          setList({
            names: (data?.branches ?? []).map((b) => b.name),
            defaultBranch: data?.defaultBranch ?? '',
          });
      })
      .catch(() => alive && setList({ names: [], defaultBranch: '' }));
    setCommits(null);
    api
      .GET('/api/git/projects/{slug}/code/log', {
        params: { path: { slug }, query: { ref: logBranch, limit: 100 } },
      })
      .then(
        ({ data }) =>
          alive &&
          setCommits(((data as { commits?: LogCommit[] } | undefined)?.commits ?? []) as LogCommit[]),
      )
      .catch(() => alive && setCommits([]));
    return () => {
      alive = false;
    };
  }, [open, slug, logBranch, atCommit]);

  const go = (ref: string) => {
    setOpen(false);
    if (ref === branch) return;
    const rest = parseProjectPath(location.pathname).rest;
    navigate(projectPath(rest, slug, ref) + location.search);
  };
  const needle = q.trim().toLowerCase();
  const shown = (list?.names ?? []).filter((n) => n.toLowerCase().includes(needle));
  const shownCommits = (commits ?? []).filter(
    (c) =>
      !needle ||
      c.message.toLowerCase().includes(needle) ||
      c.sha.startsWith(needle) ||
      c.author.name.toLowerCase().includes(needle) ||
      c.author.email.toLowerCase().includes(needle),
  );
  const TAB = 'flex-1 h-7 text-xs rounded-[5px] border-0 cursor-pointer';

  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          title={
            atCommit
              ? `Viewing commit ${info?.short ?? ''} (read-only): switch branch or commit`
              : 'Switch branch or commit'
          }
          data-testid="branch-switcher-chip"
          className={`${CHIP} border-0 cursor-pointer`}
        >
          {atCommit ? (
            <GitCommitHorizontal size={14} className="shrink-0" />
          ) : (
            <GitBranch size={14} className="shrink-0" />
          )}
          <span
            className={`font-medium truncate max-w-[120px] ${atCommit ? 'font-mono' : ''}`}
            data-testid="chip-branch"
          >
            {atCommit && info ? info.short : branch}
          </span>
          <ChevronDown size={12} className="shrink-0 opacity-70" />
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          data-testid="branch-switcher-menu"
          className="z-50 w-[320px] rounded-[5px] bg-card-bg shadow-[0_8px_40px_0_var(--shadow-blue-md)] py-1"
        >
          <div className="px-2 pt-1 pb-1 flex gap-1" role="tablist">
            {(['branches', 'commits'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                data-testid={`switcher-tab-${t}`}
                onClick={() => setTab(t)}
                className={`${TAB} ${tab === t ? 'bg-nav-active text-white' : 'bg-transparent text-page-muted hover:bg-card-bg-hover'}`}
              >
                {t === 'branches' ? 'Branches' : 'Commits'}
              </button>
            ))}
          </div>
          <div className="px-2 pb-1 pt-1 border-b border-card-border">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-2 text-page-muted" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key !== 'Enter') return;
                  if (tab === 'branches' && shown[0]) go(shown[0]);
                  if (tab === 'commits' && shownCommits[0]) go(shownCommits[0].sha);
                }}
                placeholder={tab === 'branches' ? 'Find a branch…' : 'Find a commit: message, sha or author…'}
                aria-label={tab === 'branches' ? 'Find a branch' : 'Find a commit'}
                data-testid="branch-switcher-search"
                className="w-full text-sm rounded-[5px] border border-page-divider bg-card-bg pl-7 pr-2 py-1.5 text-page-text"
              />
            </div>
          </div>
          <div className="max-h-[50vh] overflow-y-auto py-1">
            {tab === 'branches' ? (
              list === null ? (
                <div className="px-3 py-2 text-xs text-page-muted">Loading…</div>
              ) : !shown.length ? (
                <div className="px-3 py-2 text-xs text-page-muted">No branch matches</div>
              ) : (
                shown.map((n) => (
                  <DropdownMenuPrimitive.Item
                    key={n}
                    onSelect={() => go(n)}
                    className={MENU_ITEM_CLASS}
                    data-testid={`branch-switcher-item-${n}`}
                  >
                    <span className="w-4 shrink-0">{n === branch && <Check size={13} />}</span>
                    <span className="truncate">{n}</span>
                    {n === list.defaultBranch && (
                      <span className="ml-auto text-[10px] px-1.5 rounded-full border border-card-border text-page-muted shrink-0">
                        default
                      </span>
                    )}
                  </DropdownMenuPrimitive.Item>
                ))
              )
            ) : commits === null ? (
              <div className="px-3 py-2 text-xs text-page-muted">Loading…</div>
            ) : !shownCommits.length ? (
              <div className="px-3 py-2 text-xs text-page-muted">No commit matches</div>
            ) : (
              <>
                <div className="px-3 pb-1 text-[11px] text-page-muted">Recent commits of {logBranch}</div>
                {shownCommits.map((c, i) => (
                  <DropdownMenuPrimitive.Item
                    key={c.sha}
                    // The newest commit is the branch's head: picking it opens the branch itself (writable),
                    // not a read-only commit (Board #2610).
                    onSelect={() => go(c.sha === commits?.[0]?.sha ? logBranch : c.sha)}
                    className={`${MENU_ITEM_CLASS} items-start`}
                    data-testid={`commit-switcher-item-${c.sha.slice(0, 7)}`}
                    title={c.message}
                  >
                    <span className="w-4 shrink-0 pt-0.5">{info?.sha === c.sha && <Check size={13} />}</span>
                    <span className="min-w-0 flex-1 flex flex-col">
                      <span className="truncate text-sm">{c.message.split('\n')[0]}</span>
                      <span className="text-[11px] text-page-muted truncate">
                        <code className="font-mono">{c.sha.slice(0, 7)}</code> · {c.author.name} ·{' '}
                        {agoShort(c.date)}
                        {i === 0 && ' · head'}
                      </span>
                    </span>
                  </DropdownMenuPrimitive.Item>
                ))}
              </>
            )}
          </div>
          <div className="border-t border-card-border pt-1">
            <DropdownMenuPrimitive.Item asChild>
              <Link
                to={projectPath(tab === 'branches' ? 'code/branches' : 'code/commits', slug, logBranch)}
                className={`${MENU_ITEM_CLASS} no-underline`}
              >
                {tab === 'branches' ? 'All branches →' : 'All commits →'}
              </Link>
            </DropdownMenuPrimitive.Item>
          </div>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

/**
 * Standard mode: an in-place project switcher and no branch chip (git stays out of the everyday
 * chrome). The list comes from the workspace API on first open; switching keeps the current page
 * and resets the branch to main.
 */
export function ProjectSwitcherChip({ slug, branch }: { slug: string; branch: string | null }) {
  const [open, setOpen] = React.useState(false);
  const [projects, setProjects] = React.useState<Project[] | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (!open || projects !== null) return;
    api
      .GET('/api/workspace/projects')
      .then(({ data }) => setProjects(data?.projects ?? []))
      .catch(() => setProjects([]));
  }, [open, projects]);

  const go = (p: Project) => {
    if (p.slug === slug) return;
    const rest = parseProjectPath(location.pathname).rest;
    navigate(projectPath(rest, p.slug, p.defaultBranch));
  };

  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          title="Switch project"
          data-tour="project-branch-chip"
          data-testid="project-switcher-chip"
          className={`${CHIP} border-0 cursor-pointer`}
        >
          <FolderOpen size={14} className="shrink-0" />
          <span className="font-medium truncate max-w-[140px]">{slug}</span>
          <ChevronDown size={12} className="shrink-0 opacity-70" />
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          data-testid="project-switcher-menu"
          className="z-50 min-w-[220px] max-w-[300px] max-h-[60vh] overflow-y-auto rounded-[5px] bg-card-bg shadow-[0_8px_40px_0_var(--shadow-blue-md)] py-1"
        >
          {projects === null ? (
            <div className="px-3 py-2 text-xs text-page-muted">Loading…</div>
          ) : !projects.length ? (
            <div className="px-3 py-2 text-xs text-page-muted">No projects</div>
          ) : (
            projects.map((p) => (
              <DropdownMenuPrimitive.Item
                key={p.slug}
                onSelect={() => go(p)}
                className={MENU_ITEM_CLASS}
                data-testid={`project-switcher-item-${p.slug}`}
              >
                <span className="truncate">{p.name || p.slug}</span>
                {p.slug === slug && <span className="ml-auto text-nav-active shrink-0">✓</span>}
              </DropdownMenuPrimitive.Item>
            ))
          )}
          <div className="border-t border-card-border mt-1 pt-1">
            <DropdownMenuPrimitive.Item asChild>
              <Link to={projectPath('projects', slug, branch)} className={`${MENU_ITEM_CLASS} no-underline`}>
                All projects →
              </Link>
            </DropdownMenuPrimitive.Item>
          </div>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
